import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { productService, PaginatedResponse, Product } from '@/services/productService';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { logError, logDebug } from '@/utils/errorHandler';
import { statusToIsActive, debugStatusFilter } from '@/utils/statusFilterUtils';

// Define the allowed fields for table optimization
const ALLOWED_FIELDS = [
  'id',
  'name',
  'sku',
  'category_id',
  'category_name',
  'category',
  'brand',
  'tags',
  'barcode',
  'is_active',
  'created_at',
  'updated_at',
  'family_id',
  'family_name',
  'price',
  'currency',
  'created_by',
  'is_archived',
  'primary_image_thumb',
  'primary_asset_url',
] as const;

export interface ProductsState {
  products: Product[];
  filteredData: Product[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

export interface FilterParams {
  searchTerm?: string;
  name?: string;
  sku?: string;
  category?: string | string[]; // Support multi-select
  status?: string;
  brand?: string | string[]; // Support multi-select
  minPrice?: number;
  maxPrice?: number;
  tags?: string[];
  page_size?: number;
  page?: number; // Add page parameter for pagination
  family?: string | string[]; // Support multi-select
  useFieldsOptimization?: boolean; // Flag to enable/disable optimization
  // Add new filter parameters
  barcode?: string;
  is_active?: boolean;
  created_at_from?: string;
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 500;

interface QueryParams {
  search?: string;
  name?: string;
  sku?: string;
  category?: number | string; // Support both single ID (number) and comma-separated IDs (string)
  is_active?: boolean;
  brand?: string;
  barcode?: string;
  min_price?: number;
  max_price?: number;
  created_at_from?: string;
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
  tags?: string;
  family?: number | string; // Support both single ID (number) and comma-separated IDs (string)
  page?: number;
  fields?: string;
  page_size: number;
}

function buildQueryParams(filters: FilterParams): QueryParams {
  const params: Partial<QueryParams> = {};

  if (filters.searchTerm) {
    params.search = filters.searchTerm;
  }

  if (filters.name) {
    params.name = filters.name;
  }

  if (filters.sku) {
    params.sku = filters.sku;
  }

  if (filters.category && filters.category !== 'all') {
    // Handle both single category and multiple categories
    if (Array.isArray(filters.category)) {
      // For multiple categories, send as comma-separated string of IDs
      const categoryIds = filters.category
        .map(cat => {
          // Handle both string IDs and 'uncategorized' special case
          if (cat === 'uncategorized') {
            return 'uncategorized'; // Backend should handle this special case
          }
          const id = parseInt(cat, 10);
          return isNaN(id) ? null : id;
        })
        .filter(id => id !== null);

      if (categoryIds.length > 0) {
        params.category = categoryIds.join(',');
      }
    } else {
      // Single category - handle both ID and 'uncategorized'
      if (filters.category === 'uncategorized') {
        params.category = 'uncategorized';
      } else {
        const categoryId = parseInt(filters.category, 10);
        if (!isNaN(categoryId)) {
          params.category = categoryId;
        }
      }
    }
  }

  // Handle status filter using centralized utility
  debugStatusFilter('useFetchProducts', filters as any, 'buildQueryParams');

  // Priority 1: Use is_active if explicitly set (from ProductsTable after conversion)
  if (filters.is_active !== undefined && filters.is_active !== null) {
    params.is_active = filters.is_active;
  }
  // Priority 2: Convert status string to is_active boolean
  else if (filters.status && filters.status !== 'all') {
    const isActiveValue = statusToIsActive(filters.status as any);
    if (isActiveValue !== undefined) {
      params.is_active = isActiveValue;
    }
  }
  // If neither is set or status is 'all', don't set is_active (show all products)

  if (filters.brand && filters.brand !== 'all') {
    // Handle both single brand and multiple brands
    if (Array.isArray(filters.brand)) {
      // For multiple brands, send as comma-separated string
      const validBrands = filters.brand.filter(brand => brand && brand.trim());
      if (validBrands.length > 0) {
        params.brand = validBrands.join(',');
      }
    } else {
      // Single brand
      params.brand = filters.brand;
    }
  }

  // Handle barcode filter
  if (filters.barcode) {
    params.barcode = filters.barcode;
  }

  if (filters.minPrice !== undefined && filters.minPrice !== null) {
    // Ensure we send a number, not a string, to match backend NumberFilter
    const numericValue =
      typeof filters.minPrice === 'string' ? parseFloat(filters.minPrice) : filters.minPrice;
    if (!isNaN(numericValue)) {
      params.min_price = numericValue;
    }
  }

  if (filters.maxPrice !== undefined && filters.maxPrice !== null) {
    // Ensure we send a number, not a string, to match backend NumberFilter
    const numericValue =
      typeof filters.maxPrice === 'string' ? parseFloat(filters.maxPrice) : filters.maxPrice;
    if (!isNaN(numericValue)) {
      params.max_price = numericValue;
    }
  }

  // Handle date filters
  if (filters.created_at_from) {
    params.created_at_from = filters.created_at_from;
  }

  if (filters.created_at_to) {
    params.created_at_to = filters.created_at_to;
  }

  if (filters.updated_at_from) {
    params.updated_at_from = filters.updated_at_from;
  }

  if (filters.updated_at_to) {
    params.updated_at_to = filters.updated_at_to;
  }

  if (filters.tags && filters.tags.length > 0) {
    params.tags = filters.tags.join(',');
  }

  if (filters.family && filters.family !== 'all') {
    // Handle both single family and multiple families
    if (Array.isArray(filters.family)) {
      // For multiple families, send as comma-separated string of IDs
      const familyIds = filters.family.map(fam => parseInt(fam, 10)).filter(id => !isNaN(id));

      if (familyIds.length > 0) {
        params.family = familyIds.join(',');
      }
    } else {
      // Single family - convert to number since backend expects NumberFilter
      const familyId = parseInt(filters.family, 10);
      if (!isNaN(familyId)) {
        params.family = familyId;
      }
    }
  }

  // Add page parameter from filters
  if (filters.page) {
    params.page = filters.page;
  }

  // Add fields optimization parameter if enabled (default: false for testing)
  if (filters.useFieldsOptimization === true) {
    params.fields = ALLOWED_FIELDS.join(',');
  }

  // Set page size with validation
  params.page_size = Math.min(filters.page_size ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);

  return params as QueryParams;
}

export function useFetchProducts(filters: FilterParams = {}, enabled: boolean = true) {
  // Remove unused variable
  // const effectivePageSize = Math.min(filters.page_size ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);

  const queryParams = buildQueryParams(filters);

  return useQuery({
    queryKey: ['products', queryParams],
    queryFn: async () => {
      const response = await productService.getProducts(
        queryParams,
        false, // fetchAll = false to ensure we get paginated response
        false // includeAssets = false for performance (handled by fields optimization)
      );

      // Ensure we return a properly typed response
      return response as PaginatedResponse<Product>;
    },
    enabled: enabled,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
  });
}

export function useFetchProductsOld(pagination: PaginationState, filterParams: FilterParams = {}) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<ProductsState>({
    products: [],
    filteredData: [],
    totalCount: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchData = async () => {
      setState(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Map our filter params to API query parameters
        const queryParams: Record<string, any> = {
          page: pagination.pageIndex + 1, // API uses 1-based indexing
          page_size: pagination.pageSize,
        };

        // Add search term if provided
        if (filterParams.searchTerm) {
          queryParams.search = filterParams.searchTerm;
        }

        // Add name filter if provided
        if (filterParams.name) {
          queryParams.name = filterParams.name;
        }

        // Add sku filter if provided
        if (filterParams.sku) {
          queryParams.sku = filterParams.sku;
        }

        // Add category filter if provided
        if (filterParams.category && filterParams.category !== 'all') {
          // Handle both single category and multiple categories
          if (Array.isArray(filterParams.category)) {
            // For multiple categories, use first one for now
            const categoryIds = filterParams.category
              .map(cat => parseInt(cat, 10))
              .filter(id => !isNaN(id));
            if (categoryIds.length > 0) {
              queryParams.category = categoryIds[0]; // Use first category for now
            }
          } else {
            // Convert to number since backend expects NumberFilter
            const categoryId = parseInt(filterParams.category, 10);
            if (!isNaN(categoryId)) {
              queryParams.category = categoryId;
            }
          }
        }

        // Add status filter if provided
        if (filterParams.status && filterParams.status !== 'all') {
          queryParams.is_active = filterParams.status === 'active';
        }

        // Add price range filters if provided
        if (filterParams.minPrice) {
          queryParams.min_price = filterParams.minPrice;
        }

        if (filterParams.maxPrice) {
          queryParams.max_price = filterParams.maxPrice;
        }

        // Add tags filter if provided
        if (filterParams.tags && filterParams.tags.length > 0) {
          queryParams.tags = filterParams.tags.join(',');
        }

        // Log the query parameters before making the API call
        logDebug('[useFetchProducts] calling API with query parameters', {
          component: 'useFetchProducts',
          action: 'fetchData',
          metadata: { queryParams },
        });

        // Use pagination for server-side paging
        const response = await productService.getProducts(
          queryParams,
          /* fetchAll */ false,
          /* includeAssets */ false
        );

        // Check if it's a paginated response
        if (response && 'results' in response && Array.isArray(response.results)) {
          // Update with paginated data
          setState({
            products: response.results,
            filteredData: response.results,
            totalCount: response.count || 0,
            loading: false,
            error: null,
          });
        } else if (Array.isArray(response)) {
          // Direct array response
          setState({
            products: response,
            filteredData: response,
            totalCount: response.length,
            loading: false,
            error: null,
          });
        } else {
          // Fallback case
          setState({
            products: [],
            filteredData: [],
            totalCount: 0,
            loading: false,
            error: 'Invalid response format',
          });
        }
      } catch (err) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch products',
        }));
        logError(
          'Error fetching products',
          {
            component: 'useFetchProducts',
            action: 'fetchData',
            metadata: { err, filterParams },
          },
          { showToast: false, userFacing: false }
        );
      }
    };

    fetchData();
  }, [isAuthenticated, pagination.pageIndex, pagination.pageSize, filterParams]);

  return state;
}

// Enhanced hook for on-demand detail fetching
export function useFetchProductDetail(
  productId: number | undefined,
  options?: {
    includeAssets?: boolean;
    includeAttributeGroups?: boolean;
  }
) {
  const { includeAssets = false, includeAttributeGroups = false } = options || {};

  return useQuery({
    queryKey: ['product-detail', productId, { includeAssets, includeAttributeGroups }],
    queryFn: async () => {
      if (!productId) throw new Error('Product ID is required');

      const product = await productService.getProduct(productId);

      // Fetch additional data on demand
      const additionalData: any = {};

      if (includeAssets) {
        try {
          const assets = await productService.getProductAssets(productId);
          additionalData.assets = assets;
        } catch (error) {
          logError(
            'Failed to fetch assets',
            {
              component: 'useFetchProductDetail',
              action: 'fetchAssets',
              metadata: { error, productId },
            },
            { showToast: false, userFacing: false }
          );
          additionalData.assets = [];
        }
      }

      if (includeAttributeGroups) {
        try {
          // This would be the call to fetch attribute groups
          // const attributeGroups = await productService.getProductAttributeGroups(productId)
          // additionalData.attribute_groups = attributeGroups
        } catch (error) {
          logError(
            'Failed to fetch attribute groups',
            {
              component: 'useFetchProductDetail',
              action: 'fetchAttributeGroups',
              metadata: { error, productId },
            },
            { showToast: false, userFacing: false }
          );
          additionalData.attribute_groups = [];
        }
      }

      return { ...product, ...additionalData };
    },
    enabled: !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes for detail data
    gcTime: 10 * 60 * 1000, // 10 minutes cache time for detail data
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
  });
}

// New infinite scroll hook specifically for grid view
export function useFetchProductsInfinite(filters: FilterParams = {}, enabled: boolean = true) {
  const effectivePageSize = Math.min(filters.page_size ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);

  // Build query params but remove the page parameter since infinite query handles pagination
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, ...filtersWithoutPage } = filters;
  const queryParams = buildQueryParams(filtersWithoutPage);

  return useInfiniteQuery({
    queryKey: ['products-infinite', queryParams],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await productService.getProducts(
        { ...queryParams, page: pageParam },
        false, // fetchAll = false to ensure we get paginated response
        false // includeAssets = false for performance (handled by fields optimization)
      );
      // Ensure we return a properly typed response
      return response as PaginatedResponse<Product>;
    },
    getNextPageParam: (lastPage, allPages) => {
      logDebug('getNextPageParam Debug', {
        component: 'useFetchProductsInfinite',
        action: 'getNextPageParam',
        metadata: {
          lastPage,
          isArray: Array.isArray(lastPage),
          count: lastPage?.count,
          results: lastPage?.results,
          allPagesLength: allPages.length,
          effectivePageSize,
        },
      });

      // Check if there are more pages
      if (!lastPage) return undefined;

      // Handle array response (no pagination)
      if (Array.isArray(lastPage)) {
        // If we got less than the page size, no more pages
        return lastPage.length >= effectivePageSize ? allPages.length + 1 : undefined;
      }

      // Handle paginated response object
      if (typeof lastPage === 'object' && 'count' in lastPage && 'results' in lastPage) {
        const currentPage = allPages.length; // Current page is the number of pages loaded
        const totalPages = Math.ceil(lastPage.count / effectivePageSize);
        logDebug('Infinite Pagination Debug', {
          component: 'useFetchProductsInfinite',
          action: 'getNextPageParam.pagination',
          metadata: {
            currentPage,
            totalPages,
            count: lastPage.count,
            effectivePageSize,
            hasNextPage: currentPage < totalPages,
          },
        });
        return currentPage < totalPages ? currentPage + 1 : undefined;
      }

      // Unknown format, no more pages
      logError(
        'Unknown page format in infinite query',
        {
          component: 'useFetchProductsInfinite',
          action: 'getNextPageParam.unknownFormat',
          metadata: { lastPage },
        },
        { showToast: false, userFacing: false }
      );
      return undefined;
    },
    initialPageParam: 1,
    enabled: enabled, // Always enable when requested, queryParams will always have page_size and fields
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
  });
}
